import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { postsAPI } from '../../services/api';

interface User {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  trust_rating: number;
  is_admin?: boolean;
}

export interface MediaItem {
  id: string;
  media_type: 'image' | 'video';
  url: string;
  thumbnail_url?: string;
  original_url?: string;
  position: number;
  width?: number;
  height?: number;
  duration_seconds?: number;
}

interface Post {
  id: string;
  user_id: string;
  content: string;
  image_url: string;
  location_name: string;
  latitude: number;
  longitude: number;
  is_verified_location: boolean;
  like_count: number;
  comment_count: number;
  edit_count: number;
  is_liked: boolean;
  created_at: string;
  user: User;
  media?: MediaItem[];
  distance_meters?: number;
}

export interface NewsItem {
  title: string;
  link: string;
  description: string;
  pub_date: string;
  source: string;
  image_url?: string;
  city?: string;
  state_province?: string;
  country?: string;
}

export interface FeedItem {
  type: 'post' | 'news';
  post?: Post;
  news?: NewsItem;
}

interface Notification {
  id: string;
  notification_type: string;
  post_id: string;
  message: string;
  from_user: User | null;
  read: boolean;
  created_at: string;
}

interface PostsState {
  feed: Post[];
  feedItems: FeedItem[];
  currentPost: Post | null;
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  notifications: Notification[];
  scope: string;
}

const initialState: PostsState = {
  feed: [],
  feedItems: [],
  currentPost: null,
  loading: false,
  error: null,
  total: 0,
  page: 1,
  notifications: [],
  scope: 'city',
};

export const fetchFeed = createAsyncThunk(
  'posts/fetchFeed',
  async (params: { latitude: number; longitude: number; scope?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.getFeed(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch feed');
    }
  }
);

export const createPost = createAsyncThunk(
  'posts/create',
  async (data: { content: string; image_url?: string; media_items?: any[]; latitude: number; longitude: number; location_name?: string }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.create(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create post');
    }
  }
);

export const likePost = createAsyncThunk(
  'posts/like',
  async (postId: string, { rejectWithValue }) => {
    try {
      await postsAPI.like(postId);
      return postId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to like post');
    }
  }
);

export const unlikePost = createAsyncThunk(
  'posts/unlike',
  async (postId: string, { rejectWithValue }) => {
    try {
      await postsAPI.unlike(postId);
      return postId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unlike post');
    }
  }
);

export const reportPost = createAsyncThunk(
  'posts/report',
  async ({ postId, reason, notes }: { postId: string; reason: string; notes?: string }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.report(postId, reason, notes);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to report post');
    }
  }
);

export const updatePost = createAsyncThunk(
  'posts/update',
  async ({ postId, data }: { postId: string; data: { content?: string; remove_media_ids?: string[]; add_media_items?: any[] } }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.update(postId, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update post');
    }
  }
);

export const deletePost = createAsyncThunk(
  'posts/delete',
  async (postId: string, { rejectWithValue }) => {
    try {
      await postsAPI.delete(postId);
      return postId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete post');
    }
  }
);

const postsSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    clearFeed: (state) => {
      state.feed = [];
      state.feedItems = [];
      state.page = 1;
      state.total = 0;
    },
    setScope: (state, action: PayloadAction<string>) => {
      state.scope = action.payload;
    },
    wsPostLiked: (state, action: PayloadAction<{ post_id: string; like_count: number }>) => {
      const post = state.feed.find((p) => p.id === action.payload.post_id);
      if (post) {
        post.like_count = action.payload.like_count;
      }
    },
    wsPostUnliked: (state, action: PayloadAction<{ post_id: string; like_count: number }>) => {
      const post = state.feed.find((p) => p.id === action.payload.post_id);
      if (post) {
        post.like_count = action.payload.like_count;
      }
    },
    wsNewComment: (state, action: PayloadAction<{ post_id: string; comment_count: number }>) => {
      const post = state.feed.find((p) => p.id === action.payload.post_id);
      if (post) {
        post.comment_count = action.payload.comment_count;
      }
    },
    wsPostEdited: (state, action: PayloadAction<{ post_id: string; post: Post }>) => {
      const idx = state.feed.findIndex((p) => p.id === action.payload.post_id);
      if (idx !== -1) {
        state.feed[idx] = { ...state.feed[idx], ...action.payload.post };
      }
      state.feedItems = state.feedItems.map((item) => {
        if (item.type === 'post' && item.post?.id === action.payload.post_id) {
          return { ...item, post: { ...item.post, ...action.payload.post } as any };
        }
        return item;
      });
    },
    removePost: (state, action: PayloadAction<string>) => {
      state.feed = state.feed.filter((p) => p.id !== action.payload);
      state.feedItems = state.feedItems.filter(
        (item) => !(item.type === 'post' && item.post?.id === action.payload)
      );
      state.total = Math.max(0, state.total - 1);
    },
    wsNotification: (state, action: PayloadAction<any>) => {
      state.notifications.unshift({
        id: Date.now().toString(),
        notification_type: action.payload.notification_type,
        post_id: action.payload.post_id,
        message: action.payload.message,
        from_user: action.payload.from_user,
        read: false,
        created_at: new Date().toISOString(),
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeed.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeed.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const posts = action.payload.posts || [];
        const items = action.payload.items || [];
        if (action.payload.page === 1) {
          state.feed = posts;
          state.feedItems = items;
        } else {
          state.feed = [...state.feed, ...posts];
          state.feedItems = [...state.feedItems, ...items];
        }
        state.total = action.payload.total;
        state.page = action.payload.page;
      })
      .addCase(fetchFeed.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createPost.fulfilled, (state, action: PayloadAction<Post>) => {
        state.feed = [action.payload, ...state.feed];
      })
      .addCase(likePost.fulfilled, (state, action: PayloadAction<string>) => {
        const post = state.feed.find((p) => p.id === action.payload);
        if (post) {
          post.is_liked = true;
          post.like_count += 1;
        }
      })
      .addCase(unlikePost.fulfilled, (state, action: PayloadAction<string>) => {
        const post = state.feed.find((p) => p.id === action.payload);
        if (post) {
          post.is_liked = false;
          post.like_count -= 1;
        }
      })
      .addCase(reportPost.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload.auto_hidden) {
          const postId = action.payload.post_id;
          state.feed = state.feed.filter((p) => p.id !== postId);
          state.feedItems = state.feedItems.filter(
            (item) => !(item.type === 'post' && item.post?.id === postId)
          );
          state.total = Math.max(0, state.total - 1);
        }
      })
      .addCase(updatePost.fulfilled, (state, action: PayloadAction<Post>) => {
        const idx = state.feed.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) {
          state.feed[idx] = { ...state.feed[idx], ...action.payload };
        }
        state.feedItems = state.feedItems.map((item) => {
          if (item.type === 'post' && item.post?.id === action.payload.id) {
            return { ...item, post: { ...item.post, ...action.payload } as any };
          }
          return item;
        });
      })
      .addCase(deletePost.fulfilled, (state, action: PayloadAction<string>) => {
        state.feed = state.feed.filter((p) => p.id !== action.payload);
        state.feedItems = state.feedItems.filter(
          (item) => !(item.type === 'post' && item.post?.id === action.payload)
        );
        state.total = Math.max(0, state.total - 1);
      });
  },
});

export const { clearFeed, setScope, removePost, wsPostLiked, wsPostUnliked, wsNewComment, wsPostEdited, wsNotification } = postsSlice.actions;
export default postsSlice.reducer;
